/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { useDispatch } from "@teamprevent/admin-redux";

import { Container, Button, Divider, Loader } from "semantic-ui-react";
import { createValidator } from "not-valid";
import * as moment from "moment";
import { values, flatten, uniq } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AnsweredQuestion, AnsweredQuestionWithId } from "@teamprevent/common/model";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { submitInvalidQuestionnaire, updateQuestion, removeQuestion as removeQuestionAction } from "@teamprevent/common/questionnaires/actions";
import { CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig/models";
import { ImmunisationTaskType } from "@teamprevent/common/consultations/models";
import { QuestionAnswer, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";
import { toast } from "@teamprevent/common/toasts";

import { loadVaccinationConsultation } from "../actions";
import { ImmunisationTaskModel } from "../../task/model";
import { routeIdSelector as employeeRecordIdSelector } from "../../crud/employeeRecord/selectors";
import { routeIdSelector as caseIdSelector } from "../../crud/ohCase/selectors";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { useOhCaseDetailsModel } from "../../crud/ohCase";
import { DataProtectionSection } from "../../consultations/components/DataProtectionSection";
import { ConfidentialitySection } from "../../consultations/components/ConfidentialitySection";
import { ApplicationState } from "../../applicationState";
import { DidNotAttendButton } from "../../clinicalNotes/components/DidNotAttendButton";
import { vaccineRoute, vaccines, vaccineSite, other } from "../models";
import { TasksApi } from "../../tasks/tasksApi";
import { useEmployeeRecordDetailsModel } from "../../crud/employeeRecord/hooks";
import { validateConfidentialitySection } from "../../consultations/components/confidentialitySectionValidation";

import { ImmunisationTypeTasksSelector } from "./ImmunisationTypeTasksSelector";
import { VaccinationConsultationNotesOld } from "./VaccinationConsultationNotesOld";

interface StateProps {
    customerId: string;
    taskId: string;
    ohCaseId: string;
    questions: AnsweredQuestionWithId[];
    employeeRecordId: string;
}

interface OwnProps {
    submitQuestionnaire: (questions: AnsweredQuestionWithId[], immunisationTaskTypeIds: string[]) => void;
    tasksSection: React.JSX.Element;
    tasksSectionValid: boolean;
}

type Props = StateProps & OwnProps;

export const QUESTIONNAIRE_KEY = "VaccinationConsultation";

const vaccinationConsultationTaskSection = "VaccinationConsultationTask";
const manufacturerAndDoseQuestion = { id: "ManufacturerAndDose", questionText: "Manufacturer And Dose" };
const batchNumberQuestion = { id: "BatchNumber", questionText: "Batch Number" };
const ManufacturerAndDoseOtherDetails = {
    id: "ManufacturerAndDoseOtherDetails",
    questionText: "Please specify full details on manufacturer, dose and reason given"
};
const expiryDateQuestion = { id: "ExpiryDate", questionText: "Expiry Date" };
const routeQuestion = { id: "Route", questionText: "Route" };
const siteQuestion = { id: "Site", questionText: "Site" };
const adviceLeafletProvidedQuestion = {
    id: "AdviceLeafletProvided",
    questionText: "Advice leaflet provided to the employee"
};
const consentGivenQuestionId = "ConsentToGiveVaccine";
const noVaccinesAdministeredTodayQuestionId = "NoVaccineWasGivenOrRefused";
const pleaseExplainWhyNoVaccinationsWereGivenId = "PleaseExplainWhyNoVaccinationsWereGiven";

const questionsList: CustomQuestionConfig[] = [
    manufacturerAndDoseQuestion,
    batchNumberQuestion,
    expiryDateQuestion,
    routeQuestion,
    siteQuestion,
    adviceLeafletProvidedQuestion
];

export const VaccinationConsultationNotesUnconnected: React.FunctionComponent<Props> = ({
    submitQuestionnaire: submitQuestionnaireFromProps,
    ...props
}: Props) => {
    const dispatch = useDispatch();
    const {
        employeeRecord,
        isLoaded: isEmployeeLoaded
    } = useEmployeeRecordDetailsModel({ employeeRecordId: props.employeeRecordId, customerId: props.customerId });
    const { ohCase, isLoaded: isOhCaseLoaded } = useOhCaseDetailsModel({ ohCaseId: props.ohCaseId, customerId: props.customerId });
    const task = (isOhCaseLoaded && ohCase.tasks.find(t => t.id === props.taskId)) as ImmunisationTaskModel;
    const readonly = task && task.completed;
    // We should try to refactor the question into a child component so we can do something like this instead:
    // const { updateQuestion, removeQuestion } = useQuestionnaireContainerForSection("sectionId");
    const updateAnswer = React.useCallback(
        (payload: { sectionId: string; id: string; question: AnsweredQuestion }) =>
            dispatch(updateQuestion({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })), [dispatch]);
    const removeQuestion = React.useCallback((payload: { sectionId: string; id: string }) =>
        dispatch(removeQuestionAction({ ...payload, questionnaireKey: QUESTIONNAIRE_KEY })), [dispatch]);
    const [loadingConsultation, setLoadingConsultation] = React.useState(true);
    const [loadingImmunisationTypes, setLoadingImmunisationTypes] = React.useState(true);
    const [batchDetailAnswers, setBatchDetailAnswers] = React.useState<{
        id: string;
        subQuestionId: string;
        otherSelected: boolean;
    }[]>([]);
    const [consentToGiveVaccine, setConsentToGiveVaccine] = React.useState(false);
    const [immunisationTypeTasks, setImmunisationTypeTasks] = React.useState((task && task.immunisationTypeTasks) || []);
    const [noVaccinesAdministeredToday, setNoVaccinesAdministeredToday] = React.useState(false);

    React.useEffect(() => {
        const loadConsentToGiveVaccine = () => {
            const consentQuestion = props.questions.find(q => q.id === consentGivenQuestionId);
            const consentGiven = consentQuestion && consentQuestion.answer;
            setConsentToGiveVaccine(!!consentGiven);
        };

        const loadNoVaccinesAdministeredToday = () => {
            const noVaccinesAdministeredQuestion = props.questions.find(q => q.id === noVaccinesAdministeredTodayQuestionId);
            const noVaccinesAdministered = noVaccinesAdministeredQuestion && noVaccinesAdministeredQuestion.answer;
            setNoVaccinesAdministeredToday(!!noVaccinesAdministered);
        };

        if (loadingImmunisationTypes) {
            if (task?.immunisationTypeTasks?.length > 0) {
                setImmunisationTypeTasks(task.immunisationTypeTasks);
                setLoadingImmunisationTypes(false);
            }
        }

        if (loadingConsultation) {
            if (props.questions.length > 0) {
                loadConsentToGiveVaccine();
                loadNoVaccinesAdministeredToday();

                const manufacturerAndDoseQuestionQuestions = props.questions.filter(q => q.id.includes(manufacturerAndDoseQuestion.id));
                let vaccinationConsultationAnswers: { id: string; subQuestionId: string; otherSelected: boolean }[] = [];

                if (manufacturerAndDoseQuestionQuestions.length > 0) {
                    immunisationTypeTasks?.forEach(immunisationTypeTask => {

                        const answerIds: { id: string; subQuestionId: string; otherSelected: boolean }[]= [];

                        manufacturerAndDoseQuestionQuestions
                            .filter(q => q.sectionId.slice(vaccinationConsultationTaskSection.length) === immunisationTypeTask.id)
                            .map(q => {
                                if (q.id.includes(ManufacturerAndDoseOtherDetails.id))
                                {
                                    const manufacturerAndDoseOtherDetails = q.id.slice(ManufacturerAndDoseOtherDetails.id.length);
                                    const subQuestionIndex = answerIds.findIndex(a => a.subQuestionId === manufacturerAndDoseOtherDetails);
                                    if (manufacturerAndDoseOtherDetails && subQuestionIndex >= 0)
                                    {
                                        answerIds[subQuestionIndex].otherSelected = Boolean(q.answer);
                                        return;
                                    } else {
                                        answerIds.push({
                                            id: immunisationTypeTask.id,
                                            subQuestionId: manufacturerAndDoseOtherDetails ? manufacturerAndDoseOtherDetails : manufacturerAndDoseQuestion.id,
                                            otherSelected: Boolean(q.answer)
                                        });
                                        return;
                                    }
                                }
                                answerIds.push({
                                    id: immunisationTypeTask.id,
                                    subQuestionId: q.id.slice(manufacturerAndDoseQuestion.id.length),
                                    otherSelected: false
                                });
                            });

                        vaccinationConsultationAnswers = vaccinationConsultationAnswers.concat(answerIds.filter(a => a.subQuestionId));
                    });
                } else {
                    vaccinationConsultationAnswers = immunisationTypeTasks?.map(immunisationTypeTask => {
                        return {
                            id: immunisationTypeTask.id,
                            subQuestionId: manufacturerAndDoseQuestion.id,
                            otherSelected: false
                        };
                    });
                }

                setBatchDetailAnswers(vaccinationConsultationAnswers);
                setLoadingConsultation(false);
            }
        }
    }, [loadingImmunisationTypes,
        loadingConsultation,
        props.questions,
        task?.immunisationTypeTasks,
        consentToGiveVaccine,
        immunisationTypeTasks]);

    const getBatchQuestions = (vaccinationId: string): React.ReactElement[] => {
        const batchQuestions: React.ReactElement[] = [];

        batchDetailAnswers.filter(v => v.id === vaccinationId && v.subQuestionId !== manufacturerAndDoseQuestion.id).forEach(c => {
            batchQuestions.push(
                <>
                    <DropdownQuestion
                        readonly={readonly}
                        questionConfig={{
                            ...manufacturerAndDoseQuestion,
                            id: manufacturerAndDoseQuestion.id + c.subQuestionId
                        }}
                        search={true}
                        options={vaccines}
                        mandatory
                        onAnswerChange={(answer) => onManufacturerAndDoseChanged(answer)(c.subQuestionId)}
                    />
                    {c.otherSelected &&
                        <TextQuestion
                            readonly={readonly}
                            multiline
                            mandatory
                            questionConfig={{
                                ...ManufacturerAndDoseOtherDetails,
                                id: ManufacturerAndDoseOtherDetails.id + c.subQuestionId
                            }}
                        />
                    }
                    <TextQuestion
                        inline
                        mandatory
                        readonly={readonly}
                        multiline
                        questionConfig={{ ...batchNumberQuestion, id: batchNumberQuestion.id + c.subQuestionId }}
                        validators={[createValidator<string>(value => value.length <= 100, "Batch number must be 100 characters or less")]}
                    />
                    <OldDateQuestion
                        inline
                        mandatory
                        readonly={readonly}
                        questionConfig={{ ...expiryDateQuestion, id: expiryDateQuestion.id + c.subQuestionId }}
                        validators={[createValidator(value => moment().isSameOrBefore(value, "day"), "Expiry date must be in the future")]}
                    />
                    <DropdownQuestion
                        readonly={readonly}
                        questionConfig={{ ...routeQuestion, id: routeQuestion.id + c.subQuestionId }}
                        options={vaccineRoute}
                        mandatory
                    />
                    <DropdownQuestion
                        readonly={readonly}
                        questionConfig={{ ...siteQuestion, id: siteQuestion.id + c.subQuestionId }}
                        options={vaccineSite}
                        mandatory
                    />
                    <CheckboxQuestion
                        readonly={readonly}
                        mandatory
                        addAsteriskIfMandatory
                        questionConfig={{
                            ...adviceLeafletProvidedQuestion,
                            id: adviceLeafletProvidedQuestion.id + c.subQuestionId
                        }}
                        value="AdviceLeafletProvided"
                    />
                    {!readonly &&
                        <>
                            <Button
                                onClick={() => removeBatchFromImmunisationType(c.subQuestionId)}>
                                Remove batch details
                            </Button>
                        </>
                    }
                </>
            );
        });

        return batchQuestions;
    };

    const onManufacturerAndDoseChanged = (newValue: QuestionAnswer) => (subQuestionId: string) => {
        const BatchDetailAnswers = [...batchDetailAnswers];
        const batchSubQuestionAnswerIndex = BatchDetailAnswers.findIndex(b => b.subQuestionId === subQuestionId);

        if (BatchDetailAnswers[batchSubQuestionAnswerIndex])
        {
            if (newValue === other) {
                BatchDetailAnswers[batchSubQuestionAnswerIndex].otherSelected = true;
                setBatchDetailAnswers(BatchDetailAnswers);
                return;
            }

            BatchDetailAnswers[batchSubQuestionAnswerIndex].otherSelected = false;
            setBatchDetailAnswers(BatchDetailAnswers);
        }
    };

    const loadQuestionnaire = React.useCallback(() =>
        dispatch(loadVaccinationConsultation(props.ohCaseId, props.taskId, QUESTIONNAIRE_KEY))
    , [dispatch, props.ohCaseId, props.taskId]);

    const addBatchToImmunisationType = (immunisationTypeId: string) => {
        setBatchDetailAnswers(
            [...batchDetailAnswers, {
                id: immunisationTypeId,
                subQuestionId: uuidv4(),
                otherSelected: false
            }]
        );
    };

    const removeBatchFromImmunisationType = (subQuestionId: string) => {
        const VaccinationConsultationAnswers = [...batchDetailAnswers];
        setBatchDetailAnswers(VaccinationConsultationAnswers.filter(v => v.subQuestionId !== subQuestionId));
    };

    const submitQuestionnaire = React.useCallback(async (questions: AnsweredQuestionWithId[]): Promise<boolean> => {
        if (questions.some(q => !q.isValid)) {
            dispatch(submitInvalidQuestionnaire(QUESTIONNAIRE_KEY, true));
            return false;
        }

        const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

        const validationResult = validateConfidentialitySection(questions);

        if (immunisationWorkflowImprovementsPt1 && !validationResult.isValid) {
            toast.warning(validationResult.error);
            return false;
        }

        await submitQuestionnaireFromProps(questions, immunisationTypeTasks.map(t => t.id));

        return true;
    }, [dispatch, immunisationTypeTasks, submitQuestionnaireFromProps]);

    const updateDependentQuestion = React.useCallback((sectionId: string, question: { id: string; questionText: string }, answer: string) => {
        if (answer) {
            updateAnswer({
                sectionId,
                id: question.id,
                question: { id: question.id, question: question.questionText, answer: "" }
            });
        } else {
            removeQuestion({ sectionId, id: question.id });
        }
    }, [updateAnswer, removeQuestion]);

    const onConsentChanged = React.useCallback((newValue: string) => {
        setConsentToGiveVaccine(!!newValue);
        immunisationTypeTasks.map(immunisationTypeTask => {
            questionsList.map(q => {
                updateDependentQuestion(
                    `VaccinationConsultationTask${immunisationTypeTask.id}`,
                    { ...q },
                    newValue as string);
            });
        });
    }, [immunisationTypeTasks, updateDependentQuestion]);

    const onNoVaccinesAdministeredTodayChanged = React.useCallback((newValue: string) => {
        const noVaccinesAdministeredTodayChanged = !!newValue;

        setNoVaccinesAdministeredToday(noVaccinesAdministeredTodayChanged);

        if (noVaccinesAdministeredTodayChanged) {
            onConsentChanged("");
        }
    }, [onConsentChanged]);

    const getQuestionWithId = (id: string) => {
        return props.questions.find(q => q.id === id);
    };

    const getAnswerToQuestionWithId = (id: string) => {
        const question = props.questions.find(q => q.id === id);
        return question?.answer;
    };

    const consentGivenQuestion = getQuestionWithId(consentGivenQuestionId);
    const consentGivenQuestionWasFilledInVaccinationConsentSection =
        consentGivenQuestion !== undefined
        && consentGivenQuestion.sectionId === "VaccinationConsent";
    const consentGivenQuestionWasFilledInVaccinationsSection =
        consentGivenQuestion !== undefined
        && consentGivenQuestion.sectionId === "Vaccinations";

    const noVaccinesAdministeredTodayQuestion = getQuestionWithId(noVaccinesAdministeredTodayQuestionId);
    const noVaccinesAdministeredTodayQuestionWasFilledInVaccinationsSection =
        noVaccinesAdministeredTodayQuestion !== undefined
        && noVaccinesAdministeredTodayQuestion.sectionId === "Vaccinations";

    const pleaseExplainWhyNoVaccinationsWereGivenQuestionHasAnswer = getAnswerToQuestionWithId(pleaseExplainWhyNoVaccinationsWereGivenId) !== undefined;

    const removeAppointment = React.useCallback(() => new TasksApi().removeAppointment(task.caseId, task.id), [task]);
    if (isOhCaseLoaded === false || isEmployeeLoaded === false) {
        return <Loader />;
    }

    return (
        <Container>
            <h2>
                Vaccination Consultation for {ohCase.employeeFirstName} {ohCase.employeeLastName}
            </h2>
            {
                (
                    <>
                        {
                            !readonly &&
                            <Container textAlign="right" className="dna-button-container">
                                <DidNotAttendButton
                                    customerId={props.customerId}
                                    ohCaseId={props.ohCaseId}
                                    task={task}
                                />
                            </Container>
                        }

                        <AccordionQuestionnaireContainer
                            loadQuestionnaire={loadQuestionnaire}
                            questionnaireKey={QUESTIONNAIRE_KEY}
                            readonly={readonly}
                            submitQuestionnaire={submitQuestionnaire}
                            otherActionButtons={
                                <a
                                    href={`/customers/${props.customerId}/employeeRecords/${ohCase.employeeRecordId}/immunisations`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Button content="View Imms Record" type="button" />
                                </a>
                            }
                            questionnaireType={QuestionnaireTypes.ClinicalNotes}
                            task={task}
                            removeAppointment={removeAppointment}
                        >
                            {!readonly &&
                            <AccordionQuestionnaireSection
                                id="ImmunisationTypeTasks"
                                title="Vaccination Types"
                                neverHide
                            >
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: noVaccinesAdministeredTodayQuestionId,
                                        questionText: "No vaccine(s) administered today"
                                    })}
                                    value={noVaccinesAdministeredTodayQuestionId}
                                    onAnswerChange={onNoVaccinesAdministeredTodayChanged}
                                />
                                {
                                    !noVaccinesAdministeredToday &&
                                    <>
                                        <ImmunisationTypeTasksSelector
                                            selectedTypes={immunisationTypeTasks}
                                            onSelectionChanged={setImmunisationTypeTasks}
                                            addMoreButtonText="Add more vaccines"
                                            taskTypeFilter={ImmunisationTaskType.Vaccine}
                                        />
                                        <Divider hidden />
                                    </>
                                }
                            </AccordionQuestionnaireSection>
                            }
                            <ConfidentialitySection
                                index={0}
                                ohCase={ohCase}
                                employeeRecord={employeeRecord}
                                questionnaireKey={QUESTIONNAIRE_KEY}
                                readonly={readonly}
                            />
                            <DataProtectionSection
                                index={1}
                                questionnaireKey={QUESTIONNAIRE_KEY}
                                readonly={readonly}
                            />
                            {
                                !noVaccinesAdministeredToday &&
                                (
                                    (!readonly) ||
                                    (readonly && consentGivenQuestionWasFilledInVaccinationConsentSection)
                                ) &&
                                <AccordionQuestionnaireSection id="VaccinationConsent" title="Vaccination Consent">
                                    <CheckboxQuestion
                                        mandatory
                                        addAsteriskIfMandatory
                                        readonly={readonly}
                                        questionConfig={({
                                            id: consentGivenQuestionId,
                                            questionText: "Consent to give vaccine(s) confirmed"
                                        })}
                                        value={consentGivenQuestionId}
                                        onAnswerChange={onConsentChanged}
                                    />
                                </AccordionQuestionnaireSection>
                            }
                            <AccordionQuestionnaireSection id="Vaccinations" title="Vaccinations">
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "GoodHealthAtPresent",
                                        questionText: "Are you in good health at present?"
                                    })}
                                    value="GoodHealthAtPresent"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "SufferingFromAnyIllnessLoweredYourImmunity",
                                        questionText: "Are you suffering from any illness, which has lowered your immunity?"
                                    })}
                                    value="SufferingFromAnyIllnessLoweredYourImmunity"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "ReceivingAnyMedicalTreatmentLowersImmunity",
                                        // eslint-disable-next-line max-len
                                        questionText: "Have you in the last 3 months had or are you receiving any medical treatment that lowers immunity such as chemotherapy, radiotherapy or steroids. E.g. azathioprine, cyclosporin, methotrexate, cyclophosphamide, leflunomide?"
                                    })}
                                    value="ReceivingAnyMedicalTreatmentLowersImmunity"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "TakingAnyMedication",
                                        questionText: "Are you taking any medication? If yes, please list in comments below"
                                    })}
                                    value="TakingAnyMedication"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "AllergicToAnything",
                                        questionText: "Are you allergic to anything? E.g. latex, yeast, eggs, gelatine or antibiotics?"
                                    })}
                                    value="AllergicToAnything"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "HadSevereReactionVaccine",
                                        questionText: "Have you ever had a severe reaction to a vaccine?"
                                    })}
                                    value="HadSevereReactionVaccine"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "AreYouPregnant",
                                        questionText: "Are you or do you think you are pregnant or are you breast feeding?"
                                    })}
                                    value="AreYouPregnant"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "HadAnyOtherVaccine",
                                        questionText: "Have you had any other vaccine in the last month or are you planning any?"
                                    })}
                                    value="HadAnyOtherVaccine"
                                />
                                <CheckboxQuestion
                                    readonly={readonly}
                                    questionConfig={({
                                        id: "HadBloodTransfusion",
                                        questionText: "Have you had a blood transfusion or human immunoglobulin within the last 3 months?"
                                    })}
                                    value="HadBloodTransfusion"
                                />
                                {
                                    (
                                        (readonly && consentGivenQuestionWasFilledInVaccinationsSection)
                                    ) &&
                                    <CheckboxQuestion
                                        readonly={readonly}
                                        questionConfig={({
                                            id: consentGivenQuestionId,
                                            questionText: "Consent to give vaccine confirmed"
                                        })}
                                        value={consentGivenQuestionId}
                                        onAnswerChange={onConsentChanged}
                                    />
                                }
                                <TextQuestion
                                    inline
                                    readonly={readonly}
                                    multiline
                                    questionConfig={({
                                        id: "AdditionalCommentsOnAboveContraindications",
                                        questionText: "Additional Comments on above contraindications"
                                    })}
                                    includeTextSnippetSelector
                                />
                                {
                                    (
                                        (readonly && noVaccinesAdministeredTodayQuestionWasFilledInVaccinationsSection)
                                    ) &&
                                    <CheckboxQuestion
                                        readonly={readonly}
                                        questionConfig={({
                                            id: noVaccinesAdministeredTodayQuestionId,
                                            questionText: "Tick here if no vaccines were given during this consultation or if ALL vaccines were refused"
                                        })}
                                        value={noVaccinesAdministeredTodayQuestionId}
                                    />
                                }
                                {
                                    (
                                        (!readonly && noVaccinesAdministeredToday) ||
                                        (readonly && pleaseExplainWhyNoVaccinationsWereGivenQuestionHasAnswer)
                                    ) &&
                                    <TextQuestion
                                        inline
                                        readonly={readonly}
                                        multiline
                                        questionConfig={({
                                            id: pleaseExplainWhyNoVaccinationsWereGivenId,
                                            questionText: "Please explain why no vaccinations were given"
                                        })}
                                        mandatory={true}
                                    />
                                }
                            </AccordionQuestionnaireSection>
                            { consentToGiveVaccine &&
                                immunisationTypeTasks &&
                                immunisationTypeTasks.map(immunisationTypeTask => {
                                    if (!immunisationTypeTask) {
                                        return null;
                                    }
                                    return (
                                        <AccordionQuestionnaireSection
                                            id={`VaccinationConsultationTask${immunisationTypeTask.id}`}
                                            title={immunisationTypeTask.name}
                                            key={immunisationTypeTask.id}
                                        >
                                            <DropdownQuestion
                                                readonly={readonly}
                                                questionConfig={manufacturerAndDoseQuestion}
                                                search={true}
                                                options={vaccines}
                                                sortAlphabetically={true}
                                                mandatory
                                                onAnswerChange={(answer) => onManufacturerAndDoseChanged(answer)(manufacturerAndDoseQuestion.id)}
                                            />
                                            {batchDetailAnswers.find(b => b.subQuestionId === manufacturerAndDoseQuestion.id)?.otherSelected &&
                                                <TextQuestion
                                                    readonly={readonly}
                                                    multiline
                                                    mandatory
                                                    questionConfig={ManufacturerAndDoseOtherDetails}
                                                />
                                            }
                                            <TextQuestion
                                                inline
                                                mandatory
                                                readonly={readonly}
                                                multiline
                                                questionConfig={batchNumberQuestion}
                                                validators={
                                                    [createValidator<string>(value => value.length <= 100, "Batch number must be 100 characters or less")]}
                                            />
                                            <OldDateQuestion
                                                inline
                                                mandatory
                                                readonly={readonly}
                                                questionConfig={expiryDateQuestion}
                                                validators={[createValidator(value =>
                                                    moment().isSameOrBefore(value, "day"), "Expiry date must be in the future")]}
                                            />
                                            <DropdownQuestion
                                                readonly={readonly}
                                                questionConfig={routeQuestion}
                                                options={vaccineRoute}
                                                mandatory
                                            />
                                            <DropdownQuestion
                                                readonly={readonly}
                                                questionConfig={siteQuestion}
                                                options={vaccineSite}
                                                mandatory
                                            />
                                            <CheckboxQuestion
                                                readonly={readonly}
                                                mandatory
                                                addAsteriskIfMandatory
                                                questionConfig={adviceLeafletProvidedQuestion}
                                                value="AdviceLeafletProvided"
                                            />
                                            {
                                                getBatchQuestions(immunisationTypeTask.id)
                                                    .map((item) => item)
                                            }
                                            {
                                                !readonly && (
                                                    <>
                                                        <Button
                                                            onClick={() => addBatchToImmunisationType(immunisationTypeTask.id)}>
                                                            Add additional batch details
                                                        </Button>
                                                        <Divider hidden />
                                                    </>
                                                )
                                            }
                                        </AccordionQuestionnaireSection>
                                    );
                                })}
                            {!readonly && ohCase &&
                            <AccordionQuestionnaireSection
                                id="NextTasks"
                                title="Next Tasks"
                                neverHide={true}
                                additionalIsValid={props.tasksSectionValid}
                            >
                                {props.tasksSection}
                            </AccordionQuestionnaireSection>
                            }
                        </AccordionQuestionnaireContainer>
                    </>)}
        </Container>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => {
    const taskId = state.router.params!.taskId;
    const questionnaireState = state.questionnaires[QUESTIONNAIRE_KEY];
    return {
        customerId: customerIdSelector(state),
        ohCaseId: caseIdSelector(state),
        employeeRecordId: employeeRecordIdSelector(state),
        questions: questionnaireState ? uniq(flatten(questionnaireState.sections.map(section => values(section.questions)))) : [],
        taskId,
    };
};

const VaccinationConsultationNotesNew = connect(mapStateToProps)(VaccinationConsultationNotesUnconnected);

const immunisationWorkflowImprovementsPt1 = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

export const VaccinationConsultationNotes = immunisationWorkflowImprovementsPt1 ? VaccinationConsultationNotesNew : VaccinationConsultationNotesOld;
